import { closestCenter, DndContext, DragEndEvent } from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { times } from "lodash";
import { useId } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";

import { ErrorHint } from "src/base-components/ErrorHint";
import { Input } from "src/base-components/Input";
import { ReorderHandle } from "src/base-components/ReorderHandle";
import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import { ENTITY_ID_FIELD_NAME } from "src/datasets/DatasetTable/utils";
import {
  MaskingOptions,
  AdvancedOptions,
} from "src/entities/SchemaEditor/MaskingOptions";
import { PredefinedValuesEditor } from "src/entities/SchemaEditor/PredefinedValuesEditor";
import { isPredefinedValuesProperty } from "src/entities/SchemaEditor/utils";
import { getOrderedPropertiesByKey } from "src/entities/entityView/utils";
import {
  EntitySchemaProperty,
  EntitySchemaResource,
} from "src/entities/queries";
import { PropertyTypeSelector } from "src/eventsCatalogue/SchemaEditor/PropertyTypeSelector";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";

type PropertyFieldsProps =
  | {
      propertyName: string;
      property: EntitySchemaProperty;
      schemaId: string;
      isLoading?: false;
    }
  | {
      isLoading: true;
    };

const PropertyFields: React.FC<PropertyFieldsProps> = (props) => {
  const id = useId();
  const { listeners, setNodeRef, transform, transition } = useSortable({
    id: "propertyName" in props ? props.propertyName : id,
    disabled: props.isLoading,
  });
  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
  };
  const { register, formState, getValues } =
    useFormContext<EntitySchemaResource>();

  if (props.isLoading) {
    return (
      <div className="flex gap-x-2">
        {times(3, (i) => (
          <div key={i} className="w-1/3 flex-1 shrink-0">
            <SkeletonPlaceholder
              height="h-8"
              rounded="rounded-lg"
              width="w-full"
            />
          </div>
        ))}
      </div>
    );
  }

  const { property, schemaId, propertyName } = props;
  if (!property || !propertyName) return null;

  const isPredefinedValuesProp = isPredefinedValuesProperty(property);

  const showDataObfuscation = isFeatureFlagEnabled(
    FEATURE_FLAGS.dataObfuscation,
  );

  const isValidSensitiveType =
    property._type === "string" || property._type === "address";

  return (
    <>
      <div
        ref={setNodeRef}
        className="flex flex-col gap-y-3 bg-white"
        style={style}
      >
        <div className="relative z-10 flex gap-x-2 pl-7">
          <div className="absolute left-0 flex h-8 w-7 cursor-not-allowed items-center justify-start">
            <ReorderHandle
              color="text-gray-400"
              listeners={listeners}
              size="xs"
            />
          </div>
          <div className="w-1/3 flex-1 shrink-0">
            <Input value={propertyName} disabled fullWidth monospaced />
          </div>
          <div className="w-1/3 flex-1 shrink-0">
            <Input
              errored={
                !!formState.errors.properties?.[propertyName]?._display_name
              }
              fullWidth
              {...register(`properties.${propertyName}._display_name`, {
                required: "This field is required",
              })}
            />
            {formState.errors.properties?.[propertyName]?._display_name && (
              <ErrorHint>
                {
                  formState.errors.properties?.[propertyName]?._display_name
                    ?.message
                }
              </ErrorHint>
            )}
          </div>
          <div className="flex w-1/3 flex-1 items-center justify-stretch gap-x-2">
            <Controller<EntitySchemaResource, `properties.${string}._type`>
              name={`properties.${propertyName}._type`}
              render={({ field }) => {
                const pluralDisplayName = getValues("_display_name_plural");
                const singularDisplayName = getValues("_display_name_singular");
                return (
                  <PropertyTypeSelector
                    pluralDisplayName={pluralDisplayName}
                    property={{
                      type: property._type,
                      display_name: property._display_name,
                      enum_schema: undefined,
                      relation_schema: undefined,
                    }}
                    relSchema={property._rel_schema}
                    relationCardinality={property._cardinality}
                    schemaId={schemaId}
                    singularDisplayName={singularDisplayName}
                    value={field.value}
                  />
                );
              }}
            />
            {isValidSensitiveType &&
              propertyName !== ENTITY_ID_FIELD_NAME &&
              showDataObfuscation && (
                <div className="flex w-5 items-center justify-end">
                  <AdvancedOptions
                    propertyPath={`properties.${propertyName}`}
                  />
                </div>
              )}
          </div>
        </div>
        {isPredefinedValuesProp && (
          <PredefinedValuesEditor propertyName={propertyName} />
        )}
      </div>
      {isValidSensitiveType && property._sensitive && showDataObfuscation && (
        <div className="ml-7">
          <MaskingOptions propertyPath={`properties.${propertyName}`} />
        </div>
      )}
    </>
  );
};

type PropertiesSchemaEditorProps = {
  isLoading?: boolean;
};

export const SchemaPropertiesEditor: React.FC<PropertiesSchemaEditorProps> = ({
  isLoading,
}) => {
  const form = useFormContext<EntitySchemaResource>();

  const schemaId = form.getValues("_id");
  const properties = useWatch({
    control: form.control,
    name: "properties",
  });
  const orderedFields = getOrderedPropertiesByKey(properties);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!active.id || !over?.id) return;

    // Get the current order of items
    const items = active.data.current?.sortable.items as string[];
    const oldIndex = items.indexOf(active.id as string);
    const newIndex = items.indexOf(over.id as string);

    // If the item was dropped in the same position, do nothing
    if (oldIndex === newIndex) return;

    // Create a new array with the reordered items
    const reorderedItems = [...items];
    const [movedItem] = reorderedItems.splice(oldIndex, 1);
    reorderedItems.splice(newIndex, 0, movedItem);

    // Get all properties that need to be updated with their new order
    const propertiesToUpdate = reorderedItems.map((id, index) => ({
      id,
      newOrder: index + 1, // Assuming order starts from 1
    }));

    // Update all affected properties' orders
    propertiesToUpdate.forEach(({ id, newOrder }) => {
      form.setValue(`properties.${id}._order`, newOrder, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    });
  };

  return (
    <div className="relative flex flex-col gap-y-3">
      <div className="ml-7 flex gap-x-2 text-gray-500 font-inter-normal-12px">
        <div className="w-1/3 flex-1">Name</div>
        <div className="w-1/3 flex-1">Display Name</div>
        <div className="w-1/3 flex-1">Type</div>
      </div>
      {isLoading ? (
        times(7, (i) => <PropertyFields key={i} isLoading />)
      ) : (
        <DndContext
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={orderedFields}
            strategy={verticalListSortingStrategy}
          >
            {orderedFields.map((propertyName) => (
              <PropertyFields
                key={propertyName}
                property={form.getValues(`properties.${propertyName}`)}
                propertyName={propertyName}
                schemaId={schemaId}
              />
            ))}
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
};
